import {
  Typography,
  Card,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { saveFormDataToLocal } from "../Utility/FormDataTempStorage";
import NotesTextField from "../Worklist/NotesTextField";
import GenericTextField from "../common/GenericTextField";
import GenericDatePicker from "../common/GenericDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MedicineDurationOptions, TestNames } from "../Utility/Constants";
import { MedicineList } from "../Utility/MedicineList";
import { formatAge } from "../Utility/Utility";
import { InputAdornment, Select, MenuItem } from "@mui/material";
import GenericActionButton from "../common/GenericActionButton";
import AddIcon from "@mui/icons-material/Add";

function GeneralPrescriptionDisplay({
  formData,
  readOnly,
  setFormData,
  uniqueId,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ── helpers ──────────────────────────────────────────────────────────────

  const handleFormDataChange = (updatedValues) => {
    saveFormDataToLocal(uniqueId, updatedValues);
  };

  const getSelectedItems = (items) => {
    if (!items) return "";
    return items
      .filter((item) => item.selected)
      .map((item) => item.displayValue)
      .join(", ");
  };

  const renderReadOnlyValue = (label, value) => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
        {value || `No ${label.toLowerCase()} recorded`}
      </Typography>
    </Box>
  );

  // ── Medicine Card (mobile) ────────────────────────────────────────────────

  const MedicineCard = ({ med }) => (
    <Card className="outerCard" variant="outlined" sx={{ mb: 2, p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          #{med.serialNo}
        </Typography>
        <IconButton
          color="error"
          size="small"
          onClick={() => {
            const filtered = formData.medicines?.filter(
              (m) => m.serialNo !== med.serialNo,
            );
            const next = filtered.map((m, i) => ({ ...m, serialNo: i + 1 }));
            const updated = { ...formData, medicines: next };
            setFormData(updated);
            handleFormDataChange(updated);
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Autocomplete
        freeSolo
        options={MedicineList?.map((m) => m.name)}
        value={med.name || ""}
        onChange={(_, val) => updateMedicine(med.serialNo, "name", val)}
        onInputChange={(_, val) => updateMedicine(med.serialNo, "name", val)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Medicine"
            fullWidth
            size="small"
            margin="normal"
          />
        )}
      />
      <Autocomplete
        freeSolo
        options={MedicineDurationOptions}
        value={med.duration || ""}
        onChange={(_, val) => updateMedicine(med.serialNo, "duration", val)}
        onInputChange={(_, val) =>
          updateMedicine(med.serialNo, "duration", val)
        }
        renderInput={(params) => (
          <TextField {...params} label="Duration" fullWidth size="small" />
        )}
      />
      <Autocomplete
        freeSolo
        options={["1-0-0", "1-0-1", "1-1-1", "0-0-1"]}
        value={med.frequency || ""}
        onChange={(_, val) => updateMedicine(med.serialNo, "frequency", val)}
        onInputChange={(_, val) =>
          updateMedicine(med.serialNo, "frequency", val)
        }
        renderInput={(params) => (
          <TextField {...params} label="Frequency" fullWidth size="small" />
        )}
      />
      <TextField
        label="Instructions"
        fullWidth
        size="small"
        margin="normal"
        multiline
        rows={2}
        value={med.instructions || ""}
        onChange={(e) =>
          updateMedicine(med.serialNo, "instructions", e.target.value)
        }
      />
    </Card>
  );

  // ── Test Card (mobile) ────────────────────────────────────────────────────

  const TestCard = ({ test }) => (
    <Card className="outerCard" variant="outlined" sx={{ mb: 2, p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          #{test.serialNo}
        </Typography>
        <IconButton
          color="error"
          size="small"
          onClick={() => {
            const filtered = formData.tests?.filter(
              (t) => t.serialNo !== test.serialNo,
            );
            const next = filtered.map((t, i) => ({ ...t, serialNo: i + 1 }));
            const updated = { ...formData, tests: next };
            setFormData(updated);
            handleFormDataChange(updated);
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <TextField
        label="Test Name"
        fullWidth
        size="small"
        margin="normal"
        value={test.name || ""}
        onChange={(e) => updateTest(test.serialNo, "name", e.target.value)}
      />
      <TextField
        label="Test Date"
        fullWidth
        size="small"
        margin="normal"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={test.toBeDoneOn || ""}
        onChange={(e) =>
          updateTest(test.serialNo, "toBeDoneOn", e.target.value)
        }
      />
      <TextField
        label="Instructions"
        fullWidth
        size="small"
        margin="normal"
        multiline
        rows={2}
        value={test.instructions || ""}
        onChange={(e) =>
          updateTest(test.serialNo, "instructions", e.target.value)
        }
      />
    </Card>
  );

  // ── update helpers ────────────────────────────────────────────────────────

  const updateMedicine = (serialNo, field, value) => {
    const updated = {
      ...formData,
      medicines: formData.medicines.map((m) =>
        m.serialNo === serialNo ? { ...m, [field]: value } : m,
      ),
    };
    setFormData(updated);
    handleFormDataChange(updated);
  };

  const updateTest = (serialNo, field, value) => {
    const updated = {
      ...formData,
      tests: formData.tests.map((t) =>
        t.serialNo === serialNo ? { ...t, [field]: value } : t,
      ),
    };
    setFormData(updated);
    handleFormDataChange(updated);
  };

  // ── READ-ONLY view (mirrors PrescriptionView) ─────────────────────────────

  if (readOnly) {
    return (
      <Grid2 size={12}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          {/* Vitals */}
         {/* Vitals */}
<Box mb={2}>
  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Vitals</Typography>
  <Grid2 container spacing={2}>
    {[
      { label: "Weight", value: formData?.weight, unit: formData?.weightUnit || "kg" },
      { label: "Blood Pressure", value: formData?.bloodPressure, unit: "mmHg" },
      { label: "Temperature", value: formData?.temprature, unit: formData?.tempratureUnit || "°F" },
      { label: "Blood Sugar", value: formData?.bloodSugar, unit: formData?.bloodSugarUnit || "mg/dL" },
      { label: "Height", value: formData?.height, unit: formData?.heightUnit || "cm" },
    ].map(({ label, value, unit }) => (
      <Grid2 size={2.4} key={label}>
        <Card variant="outlined" sx={{ borderRadius: 1, p: "6px 12px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", height: 40 }}>
          <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
          <Typography variant="body2" fontWeight="bold" color={value ? "primary" : "text.disabled"}>
            {value || "—"}
            {value && (
              <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                {unit}
              </Typography>
            )}
          </Typography>
        </Card>
      </Grid2>
    ))}
  </Grid2>
</Box>
          {renderReadOnlyValue("Chief Complaints", formData?.complaint)}

          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Comorbidities
            </Typography>
            <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
              {getSelectedItems(formData?.comorbidities) ||
                "No comorbidities recorded"}
            </Typography>
          </Box>

          {renderReadOnlyValue(
            "Comorbidities Notes",
            formData?.comorbiditiesNotes,
          )}
          {renderReadOnlyValue(
            "Medical & Surgical History",
            formData?.medicalAndSurgicalHistory,
          )}
          {renderReadOnlyValue("Diagnosis Notes", formData?.diagnosis)}

          {/* Medicines table */}
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Medicines
            </Typography>
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, p: 2, background: "#fff" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", width: 40 }}
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Medicine</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Frequency</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Instructions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData?.medicines?.length > 0 ? (
                    formData.medicines.map((med) => (
                      <TableRow key={med.serialNo} hover>
                        <TableCell align="center">{med.serialNo}</TableCell>
                        <TableCell>{med.name || "-"}</TableCell>
                        <TableCell>{med.duration || "-"}</TableCell>
                        <TableCell>{med.frequency || "-"}</TableCell>
                        <TableCell>{med.instructions || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No medicines prescribed
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </Box>

          {/* Tests table */}
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Recommended Tests
            </Typography>
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, p: 2, background: "#fff" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", width: 40 }}
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Test Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Tentative Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Instructions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData?.tests?.length > 0 ? (
                    formData.tests.map((test) => (
                      <TableRow key={test.serialNo} hover>
                        <TableCell align="center">{test.serialNo}</TableCell>
                        <TableCell>{test.name || "-"}</TableCell>
                        <TableCell>{test.toBeDoneOn || "-"}</TableCell>
                        <TableCell>{test.instructions || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No tests recommended
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </Box>

          {renderReadOnlyValue("Additional Notes", formData?.additionalNotes)}
          {renderReadOnlyValue("Follow Up Date", formData?.followupDate)}
        </Card>
      </Grid2>
    );
  }

  // ── EDITABLE view (mirrors LaparoscopyPrescriptionForm renderEditableForm) ─

  return (
    <Grid2 size={12}>
      <Card
        className="outerCard"
        variant="outlined"
        sx={{
          width: "100%",
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        {/* Vitals */}
        {/* Vitals */}
        <Box mb={3}>
          <Typography gutterBottom>Vitals</Typography>
          <Grid2 container spacing={2}>
            {/* Weight */}
            <Grid2 size={2.4}>
              <GenericTextField
                label="Weight"
                size="small"
                fullWidth
                value={formData?.weight || ""}
                onChange={(e) => {
                  const updated = { ...formData, weight: e.target.value };
                  setFormData(updated);
                  handleFormDataChange(updated);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={formData?.weightUnit || "kg"}
                        onChange={(e) => {
                          const updated = {
                            ...formData,
                            weightUnit: e.target.value,
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          minWidth: 40,
                        }}
                      >
                        <MenuItem value="kg">kg</MenuItem>
                        <MenuItem value="lbs">lbs</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>

            {/* Blood Pressure */}
            <Grid2 size={2.4}>
              <GenericTextField
                label="Blood Pressure"
                size="small"
                fullWidth
                placeholder="120/80"
                value={formData?.bloodPressure || ""}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    bloodPressure: e.target.value,
                  };
                  setFormData(updated);
                  handleFormDataChange(updated);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">
                        mmHg
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>

            {/* Temperature */}
            <Grid2 size={2.4}>
              <GenericTextField
                label="Temperature"
                size="small"
                fullWidth
                value={formData?.temprature || ""}
                onChange={(e) => {
                  const updated = { ...formData, temprature: e.target.value };
                  setFormData(updated);
                  handleFormDataChange(updated);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={formData?.tempratureUnit || "°F"}
                        onChange={(e) => {
                          const updated = {
                            ...formData,
                            tempratureUnit: e.target.value,
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          minWidth: 40,
                        }}
                      >
                        <MenuItem value="°F">°F</MenuItem>
                        <MenuItem value="°C">°C</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>

            {/* Blood Sugar */}
            <Grid2 size={2.4}>
              <GenericTextField
                label="Blood Sugar"
                size="small"
                fullWidth
                value={formData?.bloodSugar || ""}
                onChange={(e) => {
                  const updated = { ...formData, bloodSugar: e.target.value };
                  setFormData(updated);
                  handleFormDataChange(updated);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={formData?.bloodSugarUnit || "mg/dL"}
                        onChange={(e) => {
                          const updated = {
                            ...formData,
                            bloodSugarUnit: e.target.value,
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          minWidth: 55,
                        }}
                      >
                        <MenuItem value="mg/dL">mg/dL</MenuItem>
                        <MenuItem value="mmol/L">mmol/L</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>

            {/* height */}

                <Grid2 size={2.4}>
              <GenericTextField
                label="Height"
                size="small"
                fullWidth
                value={formData?.height || ""}
                onChange={(e) => {
                  const updated = { ...formData, height: e.target.value };
                  setFormData(updated);
                  handleFormDataChange(updated);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={formData?.heightUnit || "cm"}
                        onChange={(e) => {
                          const updated = {
                            ...formData,
                            heightUnit: e.target.value,
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          minWidth: 40,
                        }}
                      >
                        <MenuItem value="cm">cm</MenuItem>
                        <MenuItem value="inches">inches</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
              </Grid2>
          </Grid2>
        </Box>

        {/* Chief Complaint */}
        {/* Chief Complaint */}
        <Box mb={3}>
          {/* <Typography  gutterBottom>
            Chief Complaints
          </Typography> */}
          <NotesTextField
            value={formData?.complaint || ""}
            onChange={(newValue) => {
              const updated = { ...formData, complaint: newValue };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            rows={3}
            label="Patient's Complaint"
          />
        </Box>

        {/* Comorbidities */}
        <Box mb={3}>
        
          <Autocomplete
            multiple
            options={formData?.comorbidities || []}
            getOptionLabel={(option) => option.displayValue}
            value={formData?.comorbidities?.filter((c) => c.selected) || []}
            onChange={(_, selectedOptions) => {
              const updated = {
                ...formData,
                comorbidities: formData.comorbidities.map((c) => ({
                  ...c,
                  selected: selectedOptions.some(
                    (sel) => sel.value === c.value,
                  ),
                })),
              };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: "4px 8px", minHeight: "auto" }}>
                <Checkbox
                  checked={selected}
                  size="small"
                  style={{ marginRight: 4, padding: 2 }}
                />
                <span style={{ fontSize: "0.875rem" }}>
                  {option.displayValue}
                </span>
              </li>
            )}
            renderInput={(params) => (
              <GenericTextField
                {...params}
                label="Comorbidities"
                placeholder="Select comorbidities"
                size="small"
                sx={{ background: "#fff", borderRadius: 2, mt: 1 }}
              />
            )}
          />
          <NotesTextField
            value={formData?.comorbiditiesNotes || ""}
            onChange={(newValue) => {
              const updated = { ...formData, comorbiditiesNotes: newValue };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            label=" Comorbidities"
            rows={2}
          />
        </Box>

        {/* Medical & Surgical History */}
        <Box mb={3}>
          {/* <Typography  gutterBottom>
            Medical & Surgical History
          </Typography> */}
          <NotesTextField
            value={formData?.medicalAndSurgicalHistory || ""}
            onChange={(newValue) => {
              const updated = {
                ...formData,
                medicalAndSurgicalHistory: newValue,
              };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            rows={2}
            label="Medical and Surgical History"
          />
        </Box>

        {/* Diagnosis */}
        <Box mb={3}>
          {/* <Typography >Diagnosis Notes</Typography> */}
          <NotesTextField
            value={formData?.diagnosis || ""}
            onChange={(newValue) => {
              const updated = { ...formData, diagnosis: newValue };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            label="Diagnosis Details"
            rows={4}
          />
        </Box>

        {/* Medicines */}
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="end">
            <Typography>Medicines</Typography>
            <GenericActionButton
  label="Add Medicine"
  tooltipText="Add a new medicine"
  icon={<AddIcon />}
  color="primary"
  onClick={() => {
    const updated = {
      ...formData,
      medicines: [
        ...(formData.medicines || []),
        { serialNo: (formData.medicines?.length || 0) + 1 },
      ],
    };
    setFormData(updated);
    handleFormDataChange(updated);
  }}
/>
          </Box>

          {isMobile ? (
            <Box>
              {formData?.medicines?.map((med) => (
                <MedicineCard key={med.serialNo} med={med} />
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto", mt: 2 }}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ boxShadow: 2, p: 2, background: "#fff" }}
              >
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ width: "5%" }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{  width: "25%" }}>
                        Medicine
                      </TableCell>
                      <TableCell sx={{ width: "20%" }}>
                        Duration
                      </TableCell>
                      <TableCell sx={{  width: "20%" }}>
                        Frequency
                      </TableCell>
                      <TableCell sx={{  width: "25%" }}>
                        Instructions
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{  width: "5%" }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData?.medicines?.map((med) => (
                      <TableRow key={med.serialNo} hover>
                        <TableCell align="center">{med.serialNo}</TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={MedicineList?.map(
                              (m) => `${m.name} ${m.strength}`,
                            )}
                            value={med.name || ""}
                            onChange={(_, val) =>
                              updateMedicine(med.serialNo, "name", val)
                            }
                            onInputChange={(_, val) =>
                              updateMedicine(med.serialNo, "name", val)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Medicine"
                                fullWidth
                                size="small"
                                margin="normal"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={MedicineDurationOptions}
                            value={med.duration || ""}
                            onChange={(_, val) =>
                              updateMedicine(med.serialNo, "duration", val)
                            }
                            onInputChange={(_, val) =>
                              updateMedicine(med.serialNo, "duration", val)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Duration"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={["1-0-0", "1-0-1", "1-1-1", "0-0-1"]}
                            value={med.frequency || ""}
                            onChange={(_, val) =>
                              updateMedicine(med.serialNo, "frequency", val)
                            }
                            onInputChange={(_, val) =>
                              updateMedicine(med.serialNo, "frequency", val)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Frequency"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={med.instructions || ""}
                            onChange={(e) =>
                              updateMedicine(
                                med.serialNo,
                                "instructions",
                                e.target.value,
                              )
                            }
                            placeholder="Instructions"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              const filtered = formData.medicines?.filter(
                                (m) => m.serialNo !== med.serialNo,
                              );
                              const next = filtered.map((m, i) => ({
                                ...m,
                                serialNo: i + 1,
                              }));
                              const updated = { ...formData, medicines: next };
                              setFormData(updated);
                              handleFormDataChange(updated);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
        </Box>

        {/* Recommended Tests */}
        <Box mt={3} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="end">
            <Typography>Recommended Tests</Typography>
            <GenericActionButton
  label="Add Test"
  tooltipText="Add a new recommended test"
  icon={<AddIcon />}
  color="primary"
  onClick={() => {
    const updated = {
      ...formData,
      tests: [
        ...(formData.tests || []),
        { serialNo: (formData.tests?.length || 0) + 1 },
      ],
    };
    setFormData(updated);
    handleFormDataChange(updated);
  }}
/>
          </Box>

          {isMobile ? (
            <Box>
              {formData?.tests?.map((test) => (
                <TestCard key={test.serialNo} test={test} />
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto", mt: 2 }}>
              <Card
                variant="outlined"
                sx={{ borderRadius: 3, p: 2, background: "#fff" }}
              >
                <Table sx={{ minWidth: 50 }} size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{  width: "5%" }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{  width: "30%" }}>
                        Test Name
                      </TableCell>
                      <TableCell sx={{  width: "30%" }}>
                        Tentative Date of Test
                      </TableCell>
                      <TableCell sx={{  width: "30%" }}>
                        Instructions
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{  width: "5%" }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData?.tests?.map((test) => (
                      <TableRow key={test.serialNo} hover>
                        <TableCell align="center">{test.serialNo}</TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={TestNames}
                            value={test.name || ""}
                            onChange={(_, val) =>
                              updateTest(test.serialNo, "name", val)
                            }
                            onInputChange={(_, val) =>
                              updateTest(test.serialNo, "name", val)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Test Name"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <GenericDatePicker
                              label="Tentative Date"
                              value={test.toBeDoneOn || ""}
                              onChange={(formattedDate) =>
                                updateTest(
                                  test.serialNo,
                                  "toBeDoneOn",
                                  formattedDate,
                                )
                              }
                              displayFormat="DD-MMM-YYYY"
                              outputFormat="DD-MMM-YYYY"
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  size: "small",
                                  fullWidth: true,
                                  sx: {
                                    background: "#f9f9f9",
                                    borderRadius: 1,
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell>
                          <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={test.instructions || ""}
                            onChange={(e) =>
                              updateTest(
                                test.serialNo,
                                "instructions",
                                e.target.value,
                              )
                            }
                            placeholder="Instructions"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              const filtered = formData.tests?.filter(
                                (t) => t.serialNo !== test.serialNo,
                              );
                              const next = filtered.map((t, i) => ({
                                ...t,
                                serialNo: i + 1,
                              }));
                              const updated = { ...formData, tests: next };
                              setFormData(updated);
                              handleFormDataChange(updated);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
        </Box>

        {/* Additional Notes */}
        <Box mb={3}>
          {/* <Typography gutterBottom>Additional Notes</Typography> */}
          <NotesTextField
            value={formData?.additionalNotes || ""}
            onChange={(newValue) => {
              const updated = { ...formData, additionalNotes: newValue };
              setFormData(updated);
              handleFormDataChange(updated);
            }}
            label="Additional Notes"
            rows={3}
          />
        </Box>

        {/* Follow Up Date */}
        <Box mb={3}>
          <Typography gutterBottom>Follow Up Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GenericDatePicker
              label="Follow Up Date"
              value={formData?.followupDate || ""}
              onChange={(formattedDate) => {
                const updated = { ...formData, followupDate: formattedDate };
                setFormData(updated);
                handleFormDataChange(updated);
              }}
            />
          </LocalizationProvider>
        </Box>
      </Card>
    </Grid2>
  );
}

export default GeneralPrescriptionDisplay;
